'use client'

import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { usePostLogin } from '@/hooks/react-query/auth/use-post-login'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams } from 'next/navigation'
import { useRouter } from '@/i18n/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { ImageCM } from '@/components/extension/image-cm'
import { Divider } from '@/components/ui/divider'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { validateEmail } from '@/lib/utils/validation'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { __cAuthService } from '@/services/client/__c.auth.service'
import { RANKING_LABEL_VN } from '@/types/auth'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { ForgotPasswordForm } from './forgot-password-form'
import { OtpForm } from './otp-form'
import { RegisterForm } from './register-form'
import { VerifyPhone } from './verify-phone'

type LoginFormProps = {
  isPage?: boolean
  callback?: () => void
}

export function LoginForm({ isPage, callback }: LoginFormProps) {
  // get redirect url from url params
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('redirect')
  const { refetch: refetchUser } = useUserQuery()
  const router = useRouter()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const trackEvent = useTrackEvent()
  const postLogin = usePostLogin()
  const t = useTranslations('login')

  // Định nghĩa formSchema bên trong component để dùng được t
  const formSchema = z.object({
    username: z
      .string()
      .min(1, t('required_username'))
      .refine(
        (value) => {
          const isEmail = validateEmail(value)
          const phoneValidate = value?.length === 10
          return isEmail || phoneValidate
        },
        (value) => {
          if (value.includes('@')) {
            return { message: t('invalid_email') }
          }
          if (typeof +value === 'number' && !isNaN(+value)) {
            return { message: t('invalid_phone') }
          }
          return { message: t('invalid_username') }
        }
      ),
    password: z
      .string()
      .min(1, t('required_password'))
      .min(6, t('password_too_short')),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const processAfterLoginSuccess = async () => {
    // Tránh refresh toàn trang; chỉ invalidate có chọn lọc và điều hướng nếu có
    const { data: user } = await refetchUser()
    postLogin({ user, redirectUrl })

    if (user) {
      trackEvent({
        event: ETrackingEvent.UserLogin,
        user_id: user.user_id,
        user_type: RANKING_LABEL_VN[user?.ranking?.ranking_id],
      })
    }

    callback?.()

    hidePopup()

    toast.message({
      title: t('success_title'),
      message: t('success_message'),
    })

    // Không cần router.refresh(); điều hướng (nếu có) đã xử lý trong postLogin
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setError('')
      const res = await __cAuthService.login(values)

      // Nếu chưa verify phone thì hiển thị popup verify phone
      if (!res?.data?.isset_phone) {
        showPopup(
          <VerifyPhone
            user={{
              fullname: res?.data?.fullname ?? '',
              email: res?.data?.email ?? '',
              id: res?.data?.user_id ?? '',
            }}
            isConfirm={res?.data?.is_login}
            onSuccess={processAfterLoginSuccess}
          />
        )
        return
      }

      if (res?.data?.isset_phone && !res?.data?.is_login) {
        showPopup(
          <OtpForm
            phone={res?.data?.phone ?? ''}
            onSuccess={processAfterLoginSuccess}
            type='login'
            isAuth={true}
            bodyAuth={{
              user_id: res?.data?.user_id ?? '',
              fullname: res?.data?.fullname ?? '',
              email: res?.data?.email ?? '',
              phone: res?.data?.phone ?? '',
            }}
            onClose={() => hidePopup()}
          />
        )
        return
      }

      // Nếu đã verify phone thì hiển thị toast login success
      if (res.state && res?.status === 200) {
        await processAfterLoginSuccess()
      } else {
        setError(t('error'))
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message)
      } else {
        setError(t('error'))
      }
    }
  }

  const handleThirdPartyLogin =
    (provider: 'google' | 'facebook') => async () => {
      // TODO: Tạm thời cho dùng v1
      // if (provider === 'google') {
      //   window.location.href = 'https://coolmate.me/google/login'
      //   return
      // }

      // if (provider === 'facebook') {
      //   window.location.href = 'https://coolmate.me/facebook/login'
      // }

      try {
        const redirectUrl = await __cAuthService.loginThirdParty(provider)
        window.location.href = redirectUrl
      } catch (error) {
        console.error(error)
        toast.message({
          title: t('fail_title'),
          message: t('fail_message', { provider }),
        })
      }
    }

  return (
    <>
      <div className='text-neutral-800'>
        <ImageCM src={'/March2024/mceclip4_81.jpg'} width={132} height={22} />
        <h3 className='mt-4 font-criteria text-[28px] font-medium leading-8 tracking-normal text-neutral-900'>
          {t('privileges')}
        </h3>
        <p className='mt-3.5 font-sans text-xs'>{t('benefit')}</p>
        <div className='mb-4 mt-1 grid grid-cols-3 gap-3'>
          <ImageCM
            src={'/March2024/mceclip3_52.jpg'}
            width={224}
            height={88}
            className='w-full object-cover'
            alt={t('voucher_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip1_36.jpg'}
            width={224}
            height={88}
            className='w-full object-cover'
            alt={t('gift_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip2_55.jpg'}
            width={224}
            height={88}
            className='w-full object-cover'
            alt={t('cashback_alt')}
          />
        </div>
        <p className='mb-3 mt-4 font-sans text-xs font-bold'>
          {t('login_or_register')}
        </p>
        <div className='flex gap-3'>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
            onClick={handleThirdPartyLogin('google')}
            disabled={form.formState.isSubmitting}
          >
            <ImageCM
              src={'/September2023/mceclip1_21.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login google'
            />
          </Button>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
            onClick={handleThirdPartyLogin('facebook')}
            disabled={form.formState.isSubmitting}
          >
            <ImageCM
              src={'/September2023/mceclip0_86.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login facebook'
            />
          </Button>
        </div>
        <Divider text={t('or')} />
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='username'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      autoFocus
                      floating
                      label={t('email_or_phone')}
                      {...field}
                      error={form.formState.errors?.username}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />

          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    floating
                    type={showPassword ? 'text' : 'password'}
                    {...field}
                    label={t('password')}
                    error={form.formState.errors?.password}
                    suffix={
                      <button
                        type='button'
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className='flex h-6 w-6 items-center justify-center focus:outline-none'
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <Button
              type='submit'
              className='w-full'
              variant='black'
              loading={form.formState.isSubmitting}
            >
              {t('login')}
            </Button>
            {error && <p className='mt-2 text-sm text-red-500'>{error}</p>}
          </div>
        </form>
      </Form>
      <div className='mt-4 flex justify-between'>
        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/register')
            } else {
              showPopup(<RegisterForm />)
            }
          }}
        >
          {t('register_new')}
        </Button>

        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/forgot-password')
            } else {
              showPopup(<ForgotPasswordForm />)
            }
          }}
        >
          {t('forgot_password')}
        </Button>
      </div>
    </>
  )
}
